import { z } from 'zod'

import { UserType } from 'common/core/interfaces/user'
import { UserRoles } from 'common/userRoles'
import { BillingAccessErrorSchema } from 'shared/api/common/schemas/errors'

enum NotificationSetting {
  EMAIL = 'email',
  SMS = 'sms',
  MESSENGER = 'messenger',
  MOBILE_PUSH = 'mobile_push',
  TELEGRAM = 'telegram',
}

const user = z.object({
  allowed_roles: z.array(z.enum(UserRoles)),
  assigned_role: z.enum(UserRoles).nullable(),
  avatar: z.string().nullable(),
  has_billing_access: z.boolean(),
  has_facebook_tasks_to_administer_page: z.boolean(),
  has_notify_email: z.boolean(),
  isThreadsLimitChanging: z.boolean().optional(),
  is_billing_access_modifiable: z.boolean(),
  is_creator: z.boolean(),
  is_inbox_user: z.boolean(),
  is_inbox_user_modifiable: z.boolean(),
  is_notified_by_email: z.boolean(),
  is_notified_by_messenger: z.boolean(),
  is_notified_by_sms: z.boolean(),
  is_notified_by_telegram: z.boolean(),
  is_opted_in: z.boolean(),
  is_removable: z.boolean().optional(),
  is_role_updatable: z.boolean().optional(),
  is_seat_user: z.boolean(),
  lc_auto_assign_enabled: z.boolean(),
  name: z.string(),
  notify_settings: z.array(z.enum(NotificationSetting)),
  threads_limit: z.number().nullable(),
  user_id: z.number(),
  user_role: z.enum(UserRoles),
  user_type: z.enum(UserType),
})

const requestAccessUser = z.object({
  user_id: z.number(),
  name: z.string(),
  avatar: z.string().nullable(),
  user_role: z.enum(UserRoles).optional(),
  user_type: z.enum(UserType).optional(),
  is_agency: z.boolean(),
  is_fb_administer: z.boolean().optional(),
})

export const usersApiSchemas = {
  fetch: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.undefined(),
    response: z.object({
      users: z.array(user),
    }),
  },
  update: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.object({
      user_id: z.number(),
      role_name: z.enum(UserRoles),
    }),
    response: z.undefined(),
  },
  remove: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.object({
      user_id: z.number(),
    }),
    response: z.undefined(),
  },
  setBillingAccess: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.object({
      user_id: z.number(),
      billing_access: z.boolean(),
    }),
    response: z.undefined(),
  },
  setInboxUser: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.object({
      user_id: z.number(),
      is_inbox_user: z.boolean(),
    }),
    errors: {
      BillingAccessError: BillingAccessErrorSchema,
    },
    response: z.undefined(),
  },
  canSetInboxUser: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.object({
      user_id: z.number(),
      is_inbox_user: z.boolean(),
    }),
    response: z.object({
      can_set_inbox_user: z.boolean(),
    }),
    errors: {
      BillingAccessError: BillingAccessErrorSchema,
    },
  },
  canCreateInviteLink: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.undefined(),
    response: z.object({
      can_create_user: z.boolean(),
      need_show_warning: z.boolean(),
    }),
  },
  accessRequestFetch: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.undefined(),
    response: z.object({
      request_access_users: z.array(requestAccessUser).optional(),
    }),
  },
  accessRequestApprove: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.object({
      target_user_id: z.number(),
      account_user_assigned_role: z.enum(UserRoles),
    }),
    response: z.object({
      user: user.nullable(),
    }),
  },
  accessRequestDecline: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.object({
      target_user_id: z.number(),
    }),
    response: z.object({
      user: user.nullable(),
    }),
  },
  setConversationsLimit: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.object({
      limit: z.number(),
      user_ids: z.array(z.number()),
    }),
    response: z.object({
      user_ids: z.array(z.number()),
    }),
  },
  setAutoAssignEnabled: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.object({
      is_enabled: z.boolean(),
      user_ids: z.array(z.number()),
    }),
    response: z.object({
      user_ids: z.array(z.number()),
    }),
  },
  generateInviteLink: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.object({
      role_name: z.enum(UserRoles),
    }),
    response: z.object({
      link: z.string(),
    }),
  },
}
